import { useEffect, useRef, useState } from "react";
import { Pin, Send, Sparkles, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, streamPost } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useFetch } from "@/hooks/useFetch";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ErrorState, Loading, Spinner } from "@/components/States";

const NOTE_RE = /\[\[NOTE:\s*([^\]]+)\]\]/g;
const notesOf = (t) => [...(t || "").matchAll(NOTE_RE)].map((m) => m[1].trim());
const clean = (t) => (t || "").replace(NOTE_RE, "").trim();

function Comments({ boardId, selected, roomId, onCount }) {
  const { user } = useAuth();
  const { data, setData, error, loading, reload } = useFetch(`/boards/${boardId}/comments`);
  const [text, setText] = useState("");
  useEffect(() => { if (data) onCount(data); }, [data, onCount]);
  const send = async (e) => {
    e.preventDefault();
    try {
      const { data: c } = await api.post(`/boards/${boardId}/comments`, { text, element_id: selected || null, room_id: roomId });
      setData([...(data || []), c]); setText("");
    } catch (err) { toast.error(errMsg(err)); }
  };
  const del = async (c) => {
    try { await api.delete(`/comments/${c.id}`); setData(data.filter((x) => x.id !== c.id)); } catch (err) { toast.error(errMsg(err)); }
  };
  if (loading) return <Loading rows={2} />;
  if (error) return <ErrorState message={error} onRetry={reload} />;
  const list = data.filter((c) => !selected || c.element_id === selected);
  return (
    <div className="flex h-full flex-col gap-3">
      <p className="text-xs text-muted-foreground">{selected ? "Comments on the selected item" : "All comments on this board"}</p>
      <div className="flex-1 space-y-3 overflow-y-auto" data-testid="comments-list">
        {list.length === 0 && <p className="text-sm text-muted-foreground">No comments yet.</p>}
        {list.map((c) => (
          <div key={c.id} className="rounded-lg bg-secondary/70 p-3 text-sm">
            <div className="mb-1 flex items-center justify-between text-xs text-muted-foreground">
              <span><span className="font-semibold text-foreground">{c.user_name}</span> · <span className="capitalize">{c.user_role}</span></span>
              {(user.role === "admin" || c.user_id === user.id) && <button onClick={() => del(c)} aria-label="Delete comment" data-testid={`delete-comment-${c.id}`}><Trash2 className="h-3 w-3" /></button>}
            </div>
            <p className="whitespace-pre-wrap">{c.text}</p>
          </div>
        ))}
      </div>
      <form onSubmit={send} className="flex gap-2">
        <Textarea rows={2} required value={text} onChange={(e) => setText(e.target.value)} placeholder="Leave a comment" data-testid="comment-input" />
        <Button type="submit" size="icon" aria-label="Send comment" data-testid="comment-submit"><Send className="h-4 w-4" /></Button>
      </form>
    </div>
  );
}

function Msg({ m, onPin, canPin }) {
  return (
    <div className={`rounded-lg p-3 text-sm ${m.role === "user" ? "ml-6 bg-primary text-primary-foreground" : "mr-2 border bg-card"}`} data-testid={`ai-message-${m.role}`}>
      <p className="whitespace-pre-wrap leading-relaxed">{clean(m.content) || <Spinner />}</p>
      {canPin && m.role === "assistant" && notesOf(m.content).map((n, i) => (
        <button key={`${i}-${n.slice(0, 24)}`} onClick={() => onPin(n)} className="mt-2 flex w-full items-start gap-2 rounded-md border border-dashed bg-secondary/60 p-2 text-left text-xs text-stone-800 hover:bg-secondary" data-testid="ai-pin-note-button">
          <Pin className="mt-0.5 h-3 w-3 shrink-0" /> {n}
        </button>
      ))}
    </div>
  );
}

function Ai({ boardId, kind, onPin, canPin, beforeSend }) {
  const { data, setData, error, loading, reload } = useFetch(`/boards/${boardId}/ai/messages`);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const end = useRef();
  const list = (data || []).filter((m) => m.kind === kind);
  useEffect(() => { end.current?.scrollIntoView({ behavior: "smooth" }); }, [data]);

  const run = async (msg) => {
    setBusy(true);
    await beforeSend?.();
    const base = [...(data || []), { id: `u${Date.now()}`, role: "user", kind, content: msg }];
    let acc = "";
    setData([...base, { id: "streaming", role: "assistant", kind, content: "" }]);
    try {
      await streamPost(`/boards/${boardId}/ai/${kind}`, kind === "chat" ? { message: msg } : {}, (d) => {
        acc += d; setData([...base, { id: "streaming", role: "assistant", kind, content: acc }]);
      });
    } catch (e) { toast.error(e.message); setData(base); } finally { setBusy(false); }
  };
  if (loading) return <Loading rows={2} />;
  if (error) return <ErrorState message={error} onRetry={reload} />;
  return (
    <div className="flex h-full flex-col gap-3">
      <div className="flex-1 space-y-3 overflow-y-auto">
        {list.length === 0 && (
          <div className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground">
            <Sparkles className="mb-2 h-5 w-5 text-accent" />
            {kind === "chat" ? "Ask for palette ideas, material pairings or layout suggestions. The co-designer can see everything on the board." : "Get one honest senior-designer review of the whole board."}
          </div>
        )}
        {list.map((m) => <Msg key={m.id} m={m} onPin={onPin} canPin={canPin} />)}
        <div ref={end} />
      </div>
      {kind === "chat" ? (
        <form onSubmit={(e) => { e.preventDefault(); const t = text; setText(""); run(t); }} className="flex gap-2">
          <Textarea rows={2} required value={text} onChange={(e) => setText(e.target.value)} placeholder="Ask the co-designer…" data-testid="ai-co-designer-input"
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey && text.trim() && !busy) { e.preventDefault(); const t = text; setText(""); run(t); } }} />
          <Button type="submit" size="icon" disabled={busy} aria-label="Send" data-testid="ai-send-button">{busy ? <Spinner /> : <Send className="h-4 w-4" />}</Button>
        </form>
      ) : (
        <Button onClick={() => run("Critique the board")} disabled={busy} className="rounded-full" data-testid="ai-critique-button">{busy ? <Spinner /> : <Sparkles className="mr-1 h-4 w-4" />}Critique this board</Button>
      )}
    </div>
  );
}

export default function SidePanel({ boardId, selected, roomId, onPin, canPin, onCount, beforeSend }) {
  return (
    <Tabs defaultValue="ai" className="flex h-full flex-col">
      <TabsList className="grid w-full grid-cols-3">
        <TabsTrigger value="ai" data-testid="panel-tab-ai">Co-designer</TabsTrigger>
        <TabsTrigger value="critique" data-testid="panel-tab-critique">Critique</TabsTrigger>
        <TabsTrigger value="comments" data-testid="panel-tab-comments">Comments</TabsTrigger>
      </TabsList>
      <TabsContent value="ai" className="min-h-0 flex-1"><Ai boardId={boardId} kind="chat" onPin={onPin} canPin={canPin} beforeSend={beforeSend} /></TabsContent>
      <TabsContent value="critique" className="min-h-0 flex-1"><Ai boardId={boardId} kind="critique" onPin={onPin} canPin={canPin} beforeSend={beforeSend} /></TabsContent>
      <TabsContent value="comments" className="min-h-0 flex-1"><Comments boardId={boardId} selected={selected} roomId={roomId} onCount={onCount} /></TabsContent>
    </Tabs>
  );
}
